import React from 'react';
import BlueCheckActive from 'assets/icons/checkActive.svg';
import WhiteCheckboxCheck from 'assets/icons/white-checkbox-check.svg';
import {
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form';
import FieldError from './FieldError';
import { twJoin, twMerge } from 'tailwind-merge';
import FieldLabel from './FieldLabel';

export interface CheckboxProps {
  label: string | React.ReactElement;
  checked: boolean;
  onChange: (isChecked: boolean) => void;
  name?: string;
  disabled?: boolean;
  error?: boolean;
  isAlternativeColor?: boolean;
  className?: string;
}
type ControlledCheckboxProps<T extends FieldValues> = {
  name: Path<T>;
  label: string | React.ReactElement;
  isAlternativeColor?: boolean;
  disabled?: boolean;
  className?: string;
  wrapperClassName?: string;
};

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checked,
  onChange,
  name,
  disabled = false,
  error = false,
  isAlternativeColor = false,
  className,
}) => {
  const getBgToDisplay = () => {
    if (checked && isAlternativeColor) return 'bg-clc-blue';
    return 'bg-white';
  };

  const getCheckedMarkToDisplay = () => {
    if (checked && isAlternativeColor) return `url(${WhiteCheckboxCheck})`;
    if (checked) return `url(${BlueCheckActive})`;
    return undefined;
  };

  const baseClasses =
    'flex shrink-0 h-7 w-7 text-clc-blue rounded-5 border cursor-pointer appearance-none';
  const checkClass = checked ? 'bg-center bg-no-repeat' : '';

  return (
    <label className={twMerge('flex cursor-pointer items-center', className)}>
      <input
        name={name}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className={twMerge(
          baseClasses,
          checkClass,
          getBgToDisplay(),
          disabled && 'opacity-50 cursor-not-allowed',
          !disabled && error
            ? 'border-alert-negative bg-error-yellow'
            : 'input-border__gray',
        )}
        style={{
          backgroundImage: getCheckedMarkToDisplay(),
        }}
      />
      <span className="flex text-sm font-semibold items-center ml-2.5 text-med-gray-3 cursor-pointer">
        {label}
      </span>
    </label>
  );
};

const ControlledCheckbox = <T extends FieldValues>({
  name,
  label,
  disabled,
  className,
  wrapperClassName,
  ...props
}: ControlledCheckboxProps<T>) => {
  const { control } = useFormContext<T>();

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  return (
    <div
      id={name}
      className={twJoin(
        'flex cursor-pointer flex-col',
        disabled && 'opacity-50 cursor-not-allowed',
        wrapperClassName,
      )}
    >
      <Checkbox
        label={label}
        checked={field.value}
        onChange={field.onChange}
        name={field.name}
        error={!!error}
        className={className}
        disabled={disabled}
        {...props}
      />
      {error && <FieldError className="mt-1.5">{error.message}</FieldError>}
    </div>
  );
};

export default ControlledCheckbox;

export interface CheckboxOption {
  label: string | React.ReactElement;
  value: string;
  subOptions?: CheckboxOption[];
  disabled?: boolean;
}

interface CheckboxGroupProps {
  name: string;
  options: CheckboxOption[];
  values: string[];
  onChange: (values: string[]) => void;
  error?: string | null;
  disabled?: boolean;
  className?: string;
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  name,
  options,
  values,
  onChange,
  error,
  disabled,
  className,
}) => {
  const toggle = (val: string) => {
    if (values?.includes(val)) {
      onChange(values.filter((v) => v !== val));
    } else {
      onChange([...(values || []), val]);
    }
  };

  return (
    <div
      className={twJoin(
        'flex w-full text-sm font-semibold flex-col gap-2',
        className,
      )}
    >
      {options.map((opt) => (
        <Checkbox
          key={opt.value}
          name={name}
          label={opt.label}
          checked={Boolean(values?.includes(opt.value))}
          onChange={() => toggle(opt.value)}
          disabled={disabled || opt.disabled}
          error={Boolean(error)}
        />
      ))}
      {error && <FieldError>{error}</FieldError>}
    </div>
  );
};

export type ControlledCheckboxGroupProps<T extends FieldValues> = {
  name: Path<T>;
  label?: string;
  isRequired?: boolean;
  options: CheckboxOption[];
  disabled?: boolean;
  className?: string;
};

type CheckboxMultiGroupType<T extends FieldValues> = {
  name: Path<T>;
  options: CheckboxOption[];
  parentValue: string;
  parentLabel: string | React.ReactElement;
};

export const ControlledCheckboxGroup = <T extends FieldValues>({
  name,
  label,
  isRequired,
  options,
  disabled,
  className,
}: ControlledCheckboxGroupProps<T>) => {
  const { control } = useFormContext<T>();

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const values: string[] = Array.isArray(field.value) ? field.value : [];

  return (
    <div
      id={name}
      className={twJoin('flex w-full flex-col gap-2.5', className)}
    >
      {label && (
        <FieldLabel label={label} isRequired={isRequired} name={name} />
      )}

      <CheckboxGroup
        name={field.name}
        options={options}
        values={values}
        onChange={field.onChange}
        error={error?.message}
        disabled={disabled}
      />
    </div>
  );
};

export const CheckboxMultiGroup = <T extends FieldValues>({
  name,
  options,
  parentValue,
  parentLabel,
}: CheckboxMultiGroupType<T>) => {
  const { control } = useFormContext<T>();

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const values: string[] = Array.isArray(field.value) ? field.value : [];

  const toggleChild = (val: string) => {
    if (values?.includes(val)) {
      field.onChange(values.filter((v) => v !== val));
    } else {
      const set = new Set([...(values || []), val, parentValue]);
      field.onChange([...set]);
    }
  };

  const toggleParent = (val: string) => {
    const child = options.map(({ value }) => value);
    if (values?.includes(val)) {
      field.onChange(values.filter((v) => v !== val && !child.includes(v)));
    } else {
      field.onChange([...(values || []), val, ...child]);
    }
  };

  return (
    <div className="flex flex-col">
      <Checkbox
        name={name}
        label={parentLabel}
        checked={Boolean(values?.includes(parentValue))}
        onChange={() => toggleParent(parentValue)}
        error={Boolean(error)}
        isAlternativeColor
      />
      <div className="ml-8 mt-2.5 flex flex-col gap-2">
        {options.map((opt) => (
          <Checkbox
            key={opt.value}
            name={name}
            label={opt.label}
            checked={Boolean(values?.includes(opt.value))}
            onChange={() => toggleChild(opt.value)}
            error={Boolean(error)}
            isAlternativeColor
          />
        ))}
      </div>
    </div>
  );
};
